import { env } from '../config/env';
import type {
  AccessUpsertRequest,
  AccessUserDto,
  ActionResponse,
  AppDetailDto,
  RoleCatalogDto,
  ManagedRoleDto,
  ActivePresenceDto,
  DevIssueRequest,
  DevIssueResultDto,
  DevConfigDto,
  ChangelogResponse,
  NotificationsDto,
  MyIssueDto,
  OneDriveUserDto,
  OneDriveOverviewDto,
  OneDriveSummaryDto,
  ExternalShareResultDto,
  SbomDto,
  AppInventoryDto,
  AssessmentCheckDto,
  AssessmentRunDetailDto,
  AssessmentRunSummaryDto,
  AdministrativeUnitDto,
  ApprovalRequestDto,
  AuScopeAssignmentDto,
  AuScopeAssignmentRequest,
  AuditEntryDto,
  BrandingDto,
  BrandingUpdateRequest,
  CreateGroupRequest,
  CreateTeamRequest,
  CreateUserRequest,
  CreateUserResponse,
  CredentialActionRequest,
  CredentialActionResponse,
  DashboardOverviewDto,
  DeleteGroupRequest,
  GraphConnectionsDto,
  GraphSearchHitDto,
  LapsActionRequest,
  LapsActionResponse,
  ManagedDeviceDto,
  SoftwareAssignmentDto,
  SoftwareMembershipRequest,
  DeleteTeamRequest,
  GuestOverviewDto,
  GuestSettingsDto,
  InviteGuestRequest,
  RevokeGuestRequest,
  UpdateGuestRequest,
  UpdateUserAttributesRequest,
  UserAttributesDto,
  DirectoryUserDto,
  GroupDetailDto,
  GroupSummaryDto,
  LicenseDto,
  LicenseMappingDto,
  LicenseMappingUpsertRequest,
  ManualResultRequest,
  MeDto,
  PimAssignmentDto,
  PimGovernanceSettingsDto,
  PimOverviewDto,
  PimRemoveRequest,
  PimSystem,
  PimSystemFilter,
  NamingPrefixDto,
  NamingPrefixUpsertRequest,
  PagePolicyDto,
  AccessMatrixDto,
  ApplyAllResultDto,
  IamAppDto,
  IamAppUpsertRequest,
  EnterpriseAppDto,
  RoleAppRoleDto,
  RoleAppRoleRequest,
  RoleDriftOverviewDto,
  RoleUserDto,
  RoleAssignmentDto,
  RoleAssignmentRequest,
  RoleDetailDto,
  RoleDriftDto,
  RoleGroupDto,
  RoleGroupRequest,
  RoleOverviewDto,
  RoleUpsertRequest,
  IntuneAppOptionDto,
  ServiceDto,
  TeamSummaryDto,
  TeamDetailDto,
  TeamMemberAddRequest,
  TeamMemberRemoveRequest,
  TeamTypeDto,
  TeamTypeUpsertRequest,
  UpdatePagePolicyRequest,
  UserDetailDto,
  UserInsightsDto,
} from './types';

export type TokenGetter = () => Promise<string>;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Schlanker, typisierter Client für die M365 Adminbox-API. Hängt an jede Anfrage
 * das Entra-Access-Token (Bearer) an, das `getToken` liefert.
 */
export class ApiClient {
  constructor(
    private readonly getToken: TokenGetter,
    private readonly baseUrl: string = env.apiBaseUrl,
  ) {}

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    const res = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers ?? {}),
      },
    });

    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.text();
        if (body) message = body;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, message);
    }

    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  getMe() {
    return this.request<MeDto>('/api/me');
  }

  // ---- M365-Assessment ----

  getAssessmentCatalog() {
    return this.request<AssessmentCheckDto[]>('/api/assessment/catalog');
  }

  getAssessmentRuns() {
    return this.request<AssessmentRunSummaryDto[]>('/api/assessment/runs');
  }

  getAssessmentRun(id: string) {
    return this.request<AssessmentRunDetailDto>(`/api/assessment/runs/${id}`);
  }

  startAssessment() {
    return this.request<AssessmentRunDetailDto>('/api/assessment/runs', { method: 'POST' });
  }

  setAssessmentManual(runId: string, checkId: string, body: ManualResultRequest) {
    return this.request<AssessmentRunDetailDto>(
      `/api/assessment/runs/${runId}/results/${encodeURIComponent(checkId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  }

  /** Lädt eine authentifizierte Datei (Bearer-Token) als Blob inkl. Dateiname. */
  private async downloadAuthed(path: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> {
    const token = await this.getToken();
    const res = await fetch(`${this.baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      throw new ApiError(res.status, await res.text().catch(() => res.statusText));
    }
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const filename = match ? decodeURIComponent(match[1]) : fallbackName;
    return { blob: await res.blob(), filename };
  }

  exportAssessmentRun(id: string) {
    return this.downloadAuthed(`/api/assessment/runs/${id}/export`, `M365-Assessment-${id}.xlsx`);
  }

  exportAssessmentRunPdf(id: string) {
    return this.downloadAuthed(`/api/assessment/runs/${id}/export-pdf`, `M365-Assessment-${id}.pdf`);
  }

  /** Pflegt Soll-Wert/Risiko eines Katalog-Prüfpunkts. */
  updateAssessmentCheck(id: string, body: { expected?: string; risk?: string }) {
    return this.request<AssessmentCheckDto>(`/api/assessment/catalog/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  getBranding() {
    return this.request<BrandingDto>('/api/settings/branding');
  }

  updateBranding(body: BrandingUpdateRequest) {
    return this.request<BrandingDto>('/api/settings/branding', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  getServices() {
    return this.request<ServiceDto[]>('/api/services');
  }

  getLicenses() {
    return this.request<LicenseDto[]>('/api/licenses');
  }

  /** Lizenz-Mapping (Admin): alle Tenant-Lizenzen mit Standard- und Freitext-Namen. */
  getLicenseMappings() {
    return this.request<LicenseMappingDto[]>('/api/licenses/mappings');
  }

  /** Freitext-Namen einer Lizenz setzen (leer = auf Standard zurücksetzen). */
  setLicenseMapping(skuPartNumber: string, body: LicenseMappingUpsertRequest) {
    return this.request<void>(`/api/licenses/mappings/${encodeURIComponent(skuPartNumber)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // ---- PIM ----

  getPimOverview(system: PimSystemFilter = 'Entra') {
    return this.request<PimOverviewDto>(`/api/pim/overview?system=${system}`);
  }

  getPimAssignments(unusedSinceDays?: number) {
    const qs = unusedSinceDays ? `?unusedSinceDays=${unusedSinceDays}` : '';
    return this.request<PimAssignmentDto[]>(`/api/pim/assignments${qs}`);
  }

  getPimRoleMembers(system: PimSystem, scope: string, roleDefinitionId: string) {
    const qs = new URLSearchParams({ system, scope, roleDefinitionId }).toString();
    return this.request<PimAssignmentDto[]>(`/api/pim/role-members?${qs}`);
  }

  removePimAssignment(body: PimRemoveRequest) {
    return this.request<ActionResponse>('/api/pim/remove', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getPimSettings() {
    return this.request<PimGovernanceSettingsDto>('/api/governance/pim-settings');
  }

  updatePimSettings(body: PimGovernanceSettingsDto) {
    return this.request<PimGovernanceSettingsDto>('/api/governance/pim-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  searchUsers(query: string, guestsOnly = false) {
    const g = guestsOnly ? '&guestsOnly=true' : '';
    return this.request<DirectoryUserDto[]>(`/api/users/search?q=${encodeURIComponent(query)}${g}`);
  }

  /** Verifizierte Domains des Tenants (für die UPN-Bildung). */
  getDomains() {
    return this.request<string[]>('/api/directory/domains');
  }

  createUser(body: CreateUserRequest) {
    return this.request<CreateUserResponse>('/api/users', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getAuditLog(opts: { days?: number; take?: number } = {}) {
    const qs = new URLSearchParams();
    if (opts.days) qs.set('days', String(opts.days));
    if (opts.take) qs.set('take', String(opts.take));
    const q = qs.toString();
    return this.request<AuditEntryDto[]>(`/api/audit${q ? `?${q}` : ''}`);
  }

  getDashboardOverview(days = 7) {
    return this.request<DashboardOverviewDto>(`/api/dashboard/overview?days=${days}`);
  }

  getUserInsights(id: string) {
    return this.request<UserInsightsDto>(`/api/users/${id}/insights`);
  }

  // ---- Beziehungs-Graph ----

  graphSearch(q: string) {
    return this.request<GraphSearchHitDto[]>(`/api/graph/search?q=${encodeURIComponent(q)}`);
  }

  graphConnections(type: string, id: string) {
    return this.request<GraphConnectionsDto>(`/api/graph/connections?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`);
  }

  // ---- Governance ----

  getPagePolicies() {
    return this.request<PagePolicyDto[]>('/api/governance/page-policies');
  }

  updatePagePolicy(pageKey: string, body: UpdatePagePolicyRequest) {
    return this.request<PagePolicyDto>(`/api/governance/page-policies/${encodeURIComponent(pageKey)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  getNamingPrefixes() {
    return this.request<NamingPrefixDto[]>('/api/governance/naming-prefixes');
  }

  createNamingPrefix(body: NamingPrefixUpsertRequest) {
    return this.request<NamingPrefixDto>('/api/governance/naming-prefixes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateNamingPrefix(id: string, body: NamingPrefixUpsertRequest) {
    return this.request<NamingPrefixDto>(`/api/governance/naming-prefixes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  deleteNamingPrefix(id: string) {
    return this.request<void>(`/api/governance/naming-prefixes/${id}`, { method: 'DELETE' });
  }

  getTeamTypes() {
    return this.request<TeamTypeDto[]>('/api/governance/team-types');
  }

  createTeamType(body: TeamTypeUpsertRequest) {
    return this.request<TeamTypeDto>('/api/governance/team-types', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateTeamType(id: string, body: TeamTypeUpsertRequest) {
    return this.request<TeamTypeDto>(`/api/governance/team-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  deleteTeamType(id: string) {
    return this.request<void>(`/api/governance/team-types/${id}`, { method: 'DELETE' });
  }

  // ---- Teams ----

  createTeam(body: CreateTeamRequest) {
    return this.request<ActionResponse>('/api/teams', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listTeams(search?: string) {
    const qs = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return this.request<TeamSummaryDto[]>(`/api/teams${qs}`);
  }

  deleteTeam(id: string, body: DeleteTeamRequest) {
    return this.request<ActionResponse>(`/api/teams/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  }

  getTeamDetail(id: string) {
    return this.request<TeamDetailDto>(`/api/teams/${id}`);
  }

  addTeamMember(id: string, body: TeamMemberAddRequest) {
    return this.request<ActionResponse>(`/api/teams/${id}/members`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  removeTeamMember(id: string, userId: string, body: TeamMemberRemoveRequest) {
    return this.request<ActionResponse>(`/api/teams/${id}/members/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  }

  // ---- Gast (B2B-Einladungen) ----

  getGuestSettings() {
    return this.request<GuestSettingsDto>('/api/guests/settings');
  }

  listGuests() {
    return this.request<GuestOverviewDto>('/api/guests');
  }

  inviteGuest(body: InviteGuestRequest) {
    return this.request<ActionResponse>('/api/guests', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateGuest(id: string, body: UpdateGuestRequest) {
    return this.request<ActionResponse>(`/api/guests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  revokeGuest(id: string, body: RevokeGuestRequest) {
    return this.request<ActionResponse>(`/api/guests/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ---- Benutzer-Attribute (Anpassen) ----

  getUserAttributes(id: string) {
    return this.request<UserAttributesDto>(`/api/users/${id}/attributes`);
  }

  updateUserAttributes(id: string, body: UpdateUserAttributesRequest) {
    return this.request<ActionResponse>(`/api/users/${id}/attributes`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  // ---- Freigaben ----

  getMyApprovals() {
    return this.request<ApprovalRequestDto[]>('/api/approvals/mine');
  }

  getPendingApprovals() {
    return this.request<ApprovalRequestDto[]>('/api/approvals/pending');
  }

  approveRequest(id: string, comment?: string) {
    return this.request<ApprovalRequestDto>(`/api/approvals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  }

  rejectRequest(id: string, comment?: string) {
    return this.request<ApprovalRequestDto>(`/api/approvals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  }

  // ---- AU-Scope ----

  getAdministrativeUnits() {
    return this.request<AdministrativeUnitDto[]>('/api/directory/administrative-units');
  }

  getAuScope() {
    return this.request<AuScopeAssignmentDto[]>('/api/governance/au-scope');
  }

  createAuScope(body: AuScopeAssignmentRequest) {
    return this.request<AuScopeAssignmentDto>('/api/governance/au-scope', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deleteAuScope(id: string) {
    return this.request<void>(`/api/governance/au-scope/${id}`, { method: 'DELETE' });
  }

  // ---- Benutzer-Lifecycle ----

  getUser(id: string) {
    return this.request<UserDetailDto>(`/api/users/${id}`);
  }

  blockUser(id: string, ticketNumber?: string) {
    return this.request<ActionResponse>(`/api/users/${id}/block`, {
      method: 'POST',
      body: JSON.stringify({ ticketNumber }),
    });
  }

  deleteUser(id: string, ticketNumber?: string) {
    return this.request<ActionResponse>(`/api/users/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ ticketNumber }),
    });
  }

  /** Danger-Zone: Passwort-/MFA-Reset oder Temporary Access Pass. */
  resetCredentials(id: string, body: CredentialActionRequest) {
    return this.request<CredentialActionResponse>(`/api/users/${id}/credentials`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ---- Geräte (Intune) & LAPS ----

  getUserDevices(userId: string) {
    return this.request<ManagedDeviceDto[]>(`/api/devices?userId=${encodeURIComponent(userId)}`);
  }

  requestLaps(body: LapsActionRequest) {
    return this.request<LapsActionResponse>('/api/devices/laps', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ---- Software (Intune-Apps via Gruppen) ----

  getUserSoftware(userId: string) {
    return this.request<SoftwareAssignmentDto[]>(`/api/software?userId=${encodeURIComponent(userId)}`);
  }

  setSoftwareMembership(body: SoftwareMembershipRequest) {
    return this.request<ActionResponse>('/api/software/membership', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getSoftwareInventory() {
    return this.request<AppInventoryDto>('/api/software/inventory');
  }

  refreshSoftwareInventory() {
    return this.request<AppInventoryDto>('/api/software/inventory/refresh', { method: 'POST' });
  }

  getAppDetail(appId: string) {
    return this.request<AppDetailDto>(`/api/software/inventory/${encodeURIComponent(appId)}`);
  }

  // ---- In-App-Berechtigungen ----

  getAccessConfig() {
    return this.request<AccessUserDto[]>('/api/settings/access');
  }

  setUserAccess(userObjectId: string, body: AccessUpsertRequest) {
    return this.request<AccessUserDto>(`/api/settings/access/${userObjectId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  removeUserAccess(userObjectId: string) {
    return this.request<void>(`/api/settings/access/${userObjectId}`, { method: 'DELETE' });
  }

  // ---- Rollen-Berechtigungen (Variante 2) ----

  getRoleCatalog() {
    return this.request<RoleCatalogDto>('/api/settings/roles');
  }

  setRolePermissions(roleValue: string, allowedFunctions: string[]) {
    return this.request<ManagedRoleDto>(`/api/settings/roles/${roleValue}`, {
      method: 'PUT',
      body: JSON.stringify({ allowedFunctions }),
    });
  }

  // ---- Live-Präsenz / Aktivität ----

  pingActivity(path: string) {
    return this.request<void>('/api/activity/ping', {
      method: 'POST',
      body: JSON.stringify({ path }),
    });
  }

  getActiveUsers(windowMinutes = 5) {
    return this.request<ActivePresenceDto[]>(`/api/activity/active?windowMinutes=${windowMinutes}`);
  }

  // ---- Dev: Feature Requests & Bugs (GitHub-Issues) ----

  getDevConfig() {
    return this.request<DevConfigDto>('/api/dev/config');
  }

  createDevIssue(req: DevIssueRequest) {
    return this.request<DevIssueResultDto>('/api/dev/issues', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  }

  getChangelog() {
    return this.request<ChangelogResponse>('/api/dev/changelog');
  }

  // ---- Profilfoto & Benachrichtigungen ----

  /** Lädt ein Foto (Bearer) als Object-URL; null, wenn keins (204/Fehler). */
  private async photoUrl(path: string): Promise<string | null> {
    const token = await this.getToken();
    const res = await fetch(`${this.baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 204 || !res.ok) {
      return null;
    }
    const blob = await res.blob();
    return blob.size > 0 ? URL.createObjectURL(blob) : null;
  }

  /** Profilfoto des angemeldeten Nutzers als Object-URL (oder null). */
  getMyPhotoUrl(): Promise<string | null> {
    return this.photoUrl('/api/me/photo');
  }

  /** Profilfoto eines beliebigen Nutzers (admin-only) als Object-URL (oder null). */
  getUserPhotoUrl(userObjectId: string): Promise<string | null> {
    return this.photoUrl(`/api/users/${userObjectId}/photo`);
  }

  /** Vom Nutzer gemeldete Feature Requests & Bugs inkl. aktuellem Status. */
  getMyIssues() {
    return this.request<MyIssueDto[]>('/api/me/issues');
  }

  // ---- OneDrive ----

  getOneDriveUser(upn: string) {
    return this.request<OneDriveUserDto>(`/api/onedrive/user?upn=${encodeURIComponent(upn)}`);
  }

  getOneDriveUserById(userId: string) {
    return this.request<OneDriveUserDto>(`/api/onedrive/user-by-id?userId=${encodeURIComponent(userId)}`);
  }

  getOneDriveOverview() {
    return this.request<OneDriveOverviewDto>('/api/onedrive/overview');
  }

  getOneDriveSummary() {
    return this.request<OneDriveSummaryDto>('/api/onedrive/summary');
  }

  // ---- SBOM ----

  getSbom() {
    return this.request<SbomDto>('/api/sbom');
  }

  downloadSbomPdf() {
    return this.downloadAuthed('/api/sbom/pdf', 'SBOM.pdf');
  }

  scanOneDriveShares(userId: string) {
    return this.request<ExternalShareResultDto>(`/api/onedrive/shares?userId=${encodeURIComponent(userId)}`);
  }

  revokeOneDriveShare(userId: string, itemId: string, permissionId: string) {
    return this.request<void>('/api/onedrive/shares/revoke', {
      method: 'POST',
      body: JSON.stringify({ userId, itemId, permissionId }),
    });
  }

  getNotifications() {
    return this.request<NotificationsDto>('/api/notifications');
  }

  markNotificationsSeen() {
    return this.request<void>('/api/notifications/seen', { method: 'POST' });
  }

  // ---- Gruppen ----

  listGroups(search?: string) {
    const qs = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return this.request<GroupSummaryDto[]>(`/api/groups${qs}`);
  }

  getGroup(id: string) {
    return this.request<GroupDetailDto>(`/api/groups/${id}`);
  }

  addGroupMember(groupId: string, userId: string, userDisplay?: string, groupDisplay?: string) {
    return this.request<void>(`/api/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, userDisplay, groupDisplay }),
    });
  }

  removeGroupMember(groupId: string, userId: string, userDisplay?: string, groupDisplay?: string) {
    return this.request<void>(`/api/groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify({ userDisplay, groupDisplay }),
    });
  }

  createGroup(body: CreateGroupRequest) {
    return this.request<ActionResponse>('/api/groups', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deleteGroup(id: string, body: DeleteGroupRequest) {
    return this.request<ActionResponse>(`/api/groups/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  }

  // ---- IAM / Rollen ----

  getRoleOverview() {
    return this.request<RoleOverviewDto>('/api/roles/overview');
  }

  getRoleIntuneApps() {
    return this.request<IntuneAppOptionDto[]>('/api/roles/intune-apps');
  }

  searchEnterpriseApps(q: string) {
    return this.request<EnterpriseAppDto[]>(`/api/roles/enterprise-apps?q=${encodeURIComponent(q)}`);
  }

  addRoleAppRole(roleId: string, body: RoleAppRoleRequest) {
    return this.request<RoleAppRoleDto>(`/api/roles/${roleId}/approles`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  removeRoleAppRole(roleId: string, rowId: string) {
    return this.request<void>(`/api/roles/${roleId}/approles/${rowId}`, { method: 'DELETE' });
  }

  getRole(id: string) {
    return this.request<RoleDetailDto>(`/api/roles/${id}`);
  }

  createRole(body: RoleUpsertRequest) {
    return this.request<RoleDetailDto>('/api/roles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateRole(id: string, body: RoleUpsertRequest) {
    return this.request<RoleDetailDto>(`/api/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  deleteRole(id: string) {
    return this.request<void>(`/api/roles/${id}`, { method: 'DELETE' });
  }

  addRoleGroup(roleId: string, body: RoleGroupRequest) {
    return this.request<RoleGroupDto>(`/api/roles/${roleId}/groups`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  removeRoleGroup(roleId: string, rowId: string) {
    return this.request<void>(`/api/roles/${roleId}/groups/${rowId}`, { method: 'DELETE' });
  }

  addRoleAssignment(roleId: string, body: RoleAssignmentRequest) {
    return this.request<RoleAssignmentDto>(`/api/roles/${roleId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  removeRoleAssignment(roleId: string, rowId: string) {
    return this.request<void>(`/api/roles/${roleId}/assignments/${rowId}`, { method: 'DELETE' });
  }

  getRoleDrift(roleId: string) {
    return this.request<RoleDriftDto>(`/api/roles/${roleId}/drift`);
  }

  applyRole(roleId: string) {
    return this.request<RoleDriftDto>(`/api/roles/${roleId}/apply`, { method: 'POST' });
  }

  getRoleDriftOverview() {
    return this.request<RoleDriftOverviewDto>('/api/roles/drift-overview');
  }

  applyAllRoles() {
    return this.request<ApplyAllResultDto>('/api/roles/apply-all', { method: 'POST' });
  }

  getRoleAccessMatrix() {
    return this.request<AccessMatrixDto>('/api/roles/access-matrix');
  }

  getRoleAssignedUsers(roleId: string) {
    return this.request<RoleUserDto[]>(`/api/roles/${roleId}/assigned-users`);
  }

  // ---- IAM-App-Katalog (Governance) ----

  getIamApps() {
    return this.request<IamAppDto[]>('/api/governance/iam-apps');
  }

  upsertIamApp(body: IamAppUpsertRequest) {
    return this.request<IamAppDto>('/api/governance/iam-apps', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }
}
